import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { HiOutlineExclamationTriangle } from "react-icons/hi2";
import BotCard from "../components/BotCard";
import BotDetailPage from "../components/BotDetailPage";
import BotSelectionSection from "../components/BotSelectionSection";
import ActivateBotModal from "../components/ActivateBotModal";
import ActivateLSBotModal from "../components/ActivateLSBotModal";
import useAuthedApi from "../hooks/useAuthedApi";
import { useCurrentUser } from "../hooks/useCurrentUser";
import Layout from "../components/layout/Layout";

function Tabs({ active, onChange }) {
  const tabs = [
    { key: "active", label: "Simulations" },
    { key: "new",    label: "New Simulation" },
  ];
  return (
    <div className="flex items-center border-b border-gray-200 mb-6">
      {tabs.map((t) => (
        <button
          key={t.key}
          onClick={() => onChange(t.key)}
          className={`px-1 mr-6 pb-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
            active === t.key
              ? "border-[#5B3DF6] text-[#5B3DF6]"
              : "border-transparent text-[#697077] hover:text-[#21272A]"
          }`}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

function EmptyState({ onNewBot }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-10 text-center">
      <p className="text-sm text-[#697077] mb-1 font-medium">No simulations running yet.</p>
      <p className="text-xs text-[#9CA3AF] mb-4">
        Forward Testing runs your strategy with live prices — no real orders or borrowing.
      </p>
      <button
        onClick={onNewBot}
        className="text-sm font-medium text-white bg-[#5B3DF6] rounded-xl px-5 py-2.5"
      >
        + Start Simulation
      </button>
    </div>
  );
}

export default function ForwardTesting() {
  const { user, status: authStatus } = useCurrentUser();
  const { authedGet, authedPost } = useAuthedApi();

  const [bots, setBots] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [accountsLoaded, setAccountsLoaded] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [tab, setTab] = useState("active");
  const [lsModalOpen, setLsModalOpen] = useState(false);
  const [activatingDirection, setActivatingDirection] = useState(null);
  const [viewingBot, setViewingBot] = useState(null);

  const refresh = useCallback(() => {
    authedGet("/api/borrow/list/")
      .then((res) => setBots(res.data))
      .catch((err) =>
        setLoadError(err.response?.data?.error || "Failed to load bots")
      );
    authedGet("/api/borrow/credentials/list/")
      .then((res) => setAccounts(res.data))
      .catch(() => {})
      .finally(() => setAccountsLoaded(true));
  }, [authedGet]);

  useEffect(() => {
    if (authStatus === "authed") refresh();
  }, [authStatus, refresh]);

  const handleStop = async (bot) => {
    try {
      await authedPost("/api/borrow/stop/", { instance_id: bot.instance_id });
      refresh();
    } catch (err) {
      setLoadError(err.response?.data?.error || "Failed to stop bot");
    }
  };

  const handleActivateDirection = (direction) => {
    if (direction === "LS") {
      setLsModalOpen(true);
    } else {
      setActivatingDirection(direction);
    }
  };

  if (authStatus === "loading" || (authStatus === "authed" && !accountsLoaded)) {
    return <div className="min-h-screen flex items-center justify-center text-sm text-[#697077]">Loading…</div>;
  }

  if (authStatus === "anonymous") {
    return <Navigate to="/login" replace />;
  }

  if (viewingBot) {
    return (
      <Layout user={user}>
        <BotDetailPage
          bot={viewingBot}
          onBack={() => setViewingBot(null)}
          onStop={(bot) => {
            handleStop(bot);
            setViewingBot(null);
          }}
        />
      </Layout>
    );
  }

  const simBots = bots.filter((b) => b.config_snapshot?.dry_run === true);

  return (
    <Layout user={user}>
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-lg font-semibold text-[#21272A]">Forward Testing</h2>
      </div>
      <p className="text-xs text-[#697077] mb-6">
        Runs your strategy with live prices — no real orders or borrowing placed.
      </p>

      <Tabs active={tab} onChange={setTab} />

      {loadError && (
        <div className="flex items-center gap-2 text-sm text-red-600 mb-4">
          <HiOutlineExclamationTriangle className="w-4 h-4 shrink-0" />
          {loadError}
        </div>
      )}

      {tab === "active" &&
        (simBots.length === 0 ? (
          <EmptyState onNewBot={() => setTab("new")} />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {simBots.map((bot) => (
              <BotCard
                key={bot.instance_id}
                bot={bot}
                onView={setViewingBot}
                onStop={handleStop}
              />
            ))}
          </div>
        ))}

      {tab === "new" && (
        <BotSelectionSection onActivate={handleActivateDirection} />
      )}

      <ActivateBotModal
        open={!!activatingDirection}
        direction={activatingDirection || "Long"}
        onClose={() => setActivatingDirection(null)}
        accounts={accounts}
        dryRun={true}
        onStarted={() => {
          setActivatingDirection(null);
          setTab("active");
          refresh();
        }}
      />

      <ActivateLSBotModal
        open={lsModalOpen}
        onClose={() => setLsModalOpen(false)}
        accounts={accounts}
        dryRun={true}
        onStarted={() => {
          setLsModalOpen(false);
          setTab("active");
          refresh();
        }}
      />
    </Layout>
  );
}
